<template>
  <div class="donate-card">
  
    <DonateCardFundraising v-if="hasFundraising" :fundraising="fundraising" :multiplier="multiplier"/>
    <div class="flex flex-row items-center justify-between w-full mb-4">
      <a
        v-if="step === 2"
        href="#"
        class="donate-card__back"
        @click="back"
      >&lt; Back</a>
    </div>
   
    <DonateCardStepOne
      :is-blackbaud-active="isBlackbaudActive"
      :blackbaud-url="page.blackbaud_url"
      v-if="step === 1"
      @back="back"
      :contents="page.contents"
      v-model="form.amount"
      :currency-code="page.currency_code"
      :currency-on-right="page.currency_on_right"
      :payment-type.sync="form.payment_type"
      @input="$v.$touch()"
    />
    <span
      v-if="isValidationErrorsVisible && !$v.form.amount.required"
      class="text-sm text-red"
    >An amount is required</span>

    <DonateCardStepTwo
      v-else-if="step === 2"
      @back="back"
      :contents="page.contents"
      :form="form.personal_info"
      @input="(updatedInfo) => {form.personal_info = updatedInfo}"
      :amount="form.amount"
      :countries="page.countries"
      :token="page.stripe_pk"
      :valid-card.sync="form.valid_card"
      :$v="$v"
      :show-validation-errors="isValidationErrorsVisible"
    />
    <DonateCardSuccess
      :class="{'mb-6': countdown  }"
      v-if="step === 3"
      :content="page.contents.donation_confirmation"
      :donation="donation"
    />
    <div v-if="step !== 3">
      <p
        v-if="error"
        class="mb-1 text-center text-red txt-xl"
      >{{ error }}</p>
      <button
      v-if="!isBlackbaudActive"
        :class="{ 'opacity-50 cursor-not-allowed': loading }"
        class="donate-card__button donate-card__submit"
        @click="nextStep"
      >
        <span v-if="!loading">Donate</span>
        <span v-else>
          <pulse-loader
            :loading="loading"
            color="#ffffff"
          />
        </span>
      </button>
      <p class="text-sm">{{ page.contents.did_you_know }}</p>
    </div>
    <DonateCardCountdown  class="mt-2" v-if="countdown" :countdown="countdown"></DonateCardCountdown>
 
  </div>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators'
import { createToken } from 'vue-stripe-elements-plus'
import DonateCardFundraising from './DonateCardFundraising'
import DonateCardCountdown from './DonateCardCountdown'
import DonateCardStepOne from './DonateCardStepOne'
import DonateCardStepTwo from './DonateCardStepTwo'
import DonateCardSuccess from './DonateCardSuccess'
import DonationService from '@/api/DonationService'
import PaymentService from '@/api/PaymentService'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'

const dataLayer = (window.dataLayer = window.dataLayer || [])

export default {
  components: {
    DonateCardFundraising,
    DonateCardStepOne,
    DonateCardStepTwo,
    DonateCardSuccess,
    PulseLoader,
    DonateCardCountdown
  },

  props: {
    page: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      fundraising: this.page.fundraising,
      donation: null,
      error: null,
      loading: false,
      isValidationErrorsVisible: false,
      step: 1,
      form: {
        amount: '',
        payment_type: 'one-time',
        valid_card: false,
        token: '',
        personal_info: {
          title: '',
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          communication_consent: false,
          address: '',
          zip_code: '',
          city: '',
          state: '',
          country: ''
        }
      }
    }
  },

  computed: {
    isBlackbaudActive() {
      return this.form.payment_type === 'monthly' && !!this.page.blackbaud_url
    },

    countdown() {
      if (!this.page.countdown_date) {
        return false
      }
      const dateArray = this.page.countdown_date.split('-')
      // -1 to offet decemebr to actual real month (January  =0 )
      const count = new Date(dateArray[0], dateArray[1] - 1, dateArray[2])
      return count.getTime() - new Date().getTime()
    },
    multiplier() {
      return this.page.contents.multiplier
        ? Number(this.page.contents.multiplier)
        : 1
    },

    hasFundraising() {
      return this.fundraising && this.fundraising.goal !== null
    }
  },
  created() {
    this.setAmountOverride()
  },
  methods: {
    setAmountOverride() {
      const amount = parseFloat(this.$route.query['amount'])

      if (!amount || !Number.isInteger(amount)) {
        return
      }
      return (this.form.amount = amount)
    },
    back() {
      this.step = this.step - 1
      this.$v.$touch()
    },

    donate() {
      this.error = null
      createToken({
        name: `${this.form.personal_info.first_name} ${this.form.personal_info.last_name}`,
        address_line1: this.form.personal_info.address,
        address_city: this.form.personal_info.city,
        address_state: this.form.personal_info.state,
        address_zip: this.form.personal_info.zip,
        address_country: this.form.personal_info.country,
        currency: 'usd'
      })
        .then(data => {
          PaymentService.logAttempt(data.token.client_ip)
            .then(({ data: blockData }) => {
              if (blockData.data.block_clears_at) {
                this.loading = false
                this.error = 'There was an error processing your payment.'
                return
              }

              let formData = {
                donation_page_variation_slug: this.page.slug,
                stripe: {
                  amount: parseFloat(this.form.amount) * 100,
                  is_monthly: this.form.payment_type === 'monthly',
                  token: data.token.id
                },
                donor: {
                  title: this.form.personal_info.title,
                  first_name: this.form.personal_info.first_name,
                  last_name: this.form.personal_info.last_name,
                  email: this.form.personal_info.email,
                  phone: this.form.personal_info.phone,
                  communication_consent: this.form.personal_info
                    .communication_consent,
                  timezone: this.page.location.timezone,
                  billing_address: {
                    address_line1: this.form.personal_info.address,
                    city: this.form.personal_info.city,
                    region: this.form.personal_info.state,
                    postal_code: this.form.personal_info.zip_code,
                    country: this.form.personal_info.country
                  }
                }
              }

              DonationService.donate(formData)
                .then(({ data }) => {
                  if (!data.success) {
                    return
                  }
                  this.donation = data.data
                  // Increment the fundraising component value
                  const donatedAmount = parseInt(this.form.amount) * 100
                  this.fundraising.current += donatedAmount
                  //

                  const monthly = formData.stripe.is_monthly ? 'M' : ''

                  dataLayer.push({
                    event: 'donationComplete',
                    transactionId: this.donation.order_number,
                    transactionAffiliation: 'ADRA',
                    transactionTotal: this.form.amount,
                    transactionTax: 0,
                    transactionShipping: 0,
                    transactionProducts: [
                      {
                        sku: `${this.donation.template.code}${monthly}${
                          this.donation.appeal.code
                        }`,
                        name: this.donation.appeal.name,
                        category: formData.stripe.is_monthly
                          ? 'Monthly'
                          : 'Individual',
                        price: this.form.amount,
                        quantity: 1
                      }
                    ]
                  })
                  this.loading = false
                  this.step = 3
                })
                .catch(() => {
                  this.error = 'There was a problem processing your donation.'
                  this.loading = false
                })
            })
            .catch(() => {
              this.error = 'There was an internal error.'
            })
        })
        .catch(() => {
          this.error = 'There was a problem verifying your card.'
        })
    },

    nextStep() {
      // if this step has error, display them on submit
      if (this.$v.$invalid) {
        return (this.isValidationErrorsVisible = true)
      }
      // if not errors for this step, hide the next steps errors until submit
      this.isValidationErrorsVisible = false
      if (this.step < 2) {
        this.step = 2
      } else {
        this.loading = true
        this.donate()
      }
    }
  },

  validations() {
    let form = {
      amount: {
        required
      }
    }

    if (this.step === 2) {
      form = Object.assign(form, {
        valid_card(value) {
          return value.valid_card
        },
        personal_info: {
          title: { required },
          first_name: { required },
          last_name: { required },
          email: { required, email },
          address: { required },
          zip_code: { required },
          city: { required },
          state: { required },
          country: { required }
        }
      })
    }
    return { form }
  }
}
</script>

<style scoped>
.flag-image {
  @apply w-auto
        h-8;
}
</style>
